import { useNavigate } from "react-router-dom";

export default function SEOContentSection() {
  const navigate = useNavigate();

  return (
    <section className="py-16 md:py-24 bg-secondary-50">
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-12 md:mb-16 gap-6">
          <div>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-2">
              What is{" "}
              <span className="italic text-accent-600">Kakobuy Spreadsheet</span>?
            </h2>
            <p className="text-base text-foreground-600 max-w-2xl leading-relaxed mt-3">
              The Kakobuy Spreadsheet is a meticulously organized product discovery system that helps shoppers find trending fashion, sneakers, electronics, and accessories all in one place. Instead of browsing endlessly, you get curated lists updated daily with the best finds across multiple categories.
            </p>
          </div>
          <button
            onClick={() => navigate("/article/kakobuy-spreadsheet-guide")}
            className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-accent-700 border border-accent-300/60 rounded-full hover:bg-accent-50 hover:border-accent-400 transition-all cursor-pointer whitespace-nowrap shrink-0"
          >
            Read Full Guide <i className="ri-arrow-right-line text-sm"></i>
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          <div className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-accent-200/60 transition-all duration-300">
            <div className="relative h-56 overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Elegant%20minimalist%20overhead%20flat%20lay%20of%20curated%20fashion%20accessories%20including%20a%20woven%20straw%20hat%20with%20black%20ribbon%2C%20delicate%20gold%20chain%20bracelet%2C%20and%20silk%20scarf%20in%20muted%20terracotta%2C%20arranged%20artfully%20on%20a%20warm%20textured%20linen%20surface%2C%20soft%20natural%20morning%20light%20from%20a%20nearby%20window%2C%20editorial%20lifestyle%20aesthetic%2C%20clean%20uncluttered%20composition%2C%20warm%20cream%20sand%20and%20soft%20terracotta%20tones%2C%20premium%20brand%20lookbook%20quality%2C%20organic%20artistic%20layout&width=600&height=400&seq=seo-card-v2-07&orientation=landscape"
                alt="Curated fashion accessories flat lay photography"
                title="Curated fashion accessories - Kakobuy Spreadsheet"
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                loading="lazy"
              />
              <span className="absolute top-4 left-4 px-3 py-1 text-xs font-label bg-accent-500 text-background-50 rounded-full">
                Fashion
              </span>
            </div>
            <div className="p-5 md:p-6">
              <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">
                How to Find the Best Products Using Kakobuy Spreadsheet
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                Learn the insider strategies for navigating product spreadsheets efficiently. Discover how to filter by category, compare prices, and spot trending items before they sell out.
              </p>
              <button
                onClick={() => navigate("/article/kakobuy-spreadsheet-guide")}
                className="flex items-center gap-1.5 text-sm font-medium text-accent-600 hover:text-accent-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                Read Article <i className="ri-arrow-right-line text-xs"></i>
              </button>
            </div>
          </div>

          <div className="group bg-primary-500 rounded-lg overflow-hidden border border-primary-500 cursor-pointer" onClick={() => navigate("/article/cssbuy-spreadsheet-guide")}>
            <div className="p-5 md:p-8 flex flex-col justify-between h-full min-h-[320px]">
              <div>
                <span className="inline-block px-3 py-1 text-xs font-label bg-accent-500 text-background-50 rounded-full mb-6">
                  Platform Comparison
                </span>
                <h3 className="font-heading text-xl md:text-2xl font-bold text-background-50 mb-4 leading-tight">
                  CSSBuy vs Kakobuy: Which Spreadsheet Platform Wins?
                </h3>
                <p className="text-sm text-background-50/80 leading-relaxed">
                  An honest side-by-side comparison of the two most popular spreadsheet platforms. Compare features, curation quality, update frequency, and find out which one actually delivers better product discoveries.
                </p>
              </div>
              <div className="mt-6">
                <span className="flex items-center gap-2 text-sm font-medium text-accent-400 whitespace-nowrap">
                  Read Comparison <i className="ri-arrow-right-line text-sm"></i>
                </span>
              </div>
            </div>
          </div>

          <div className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-accent-200/60 transition-all duration-300">
            <div className="relative h-56 overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Clean%20minimalist%20product%20photography%20of%20a%20pair%20of%20classic%20white%20leather%20low-top%20sneakers%20with%20subtle%20beige%20suede%20accents%20on%20a%20warm%20cream%20textured%20concrete%20surface%2C%20soft%20natural%20daylight%20creating%20gentle%20diffused%20shadows%2C%20editorial%20sneaker%20photography%20aesthetic%2C%20warm%20cream%20beige%20and%20crisp%20white%20tones%2C%20premium%20sportswear%20brand%20lookbook%20style%2C%20simple%20elegant%20composition%20with%20breathing%20room%2C%20organic%20surface%20texture%20visible&width=600&height=400&seq=seo-card-v2-08&orientation=landscape"
                alt="Classic white leather sneakers product photography"
                title="Classic white sneakers - Kakobuy Spreadsheet find"
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                loading="lazy"
              />
              <span className="absolute top-4 left-4 px-3 py-1 text-xs font-label bg-accent-500 text-background-50 rounded-full">
                Power User
              </span>
            </div>
            <div className="p-5 md:p-6">
              <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">
                Superbuy Spreadsheet: Advanced Finding Strategies
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                Master advanced product discovery techniques on the Superbuy platform. Learn bulk comparison, category hierarchy navigation, and watch-list strategies used by experienced power shoppers.
              </p>
              <button
                onClick={() => navigate("/article/superbuy-spreadsheet-guide")}
                className="flex items-center gap-1.5 text-sm font-medium text-accent-600 hover:text-accent-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                Read Article <i className="ri-arrow-right-line text-xs"></i>
              </button>
            </div>
          </div>
        </div>

        <div className="mt-6 text-center">
          <button
            onClick={() => navigate("/article/oopbuy-spreadsheet-guide")}
            className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-foreground-600 border border-foreground-300/60 rounded-full hover:border-accent-400 hover:text-accent-700 transition-all cursor-pointer whitespace-nowrap"
          >
            Explore All Platform Guides <i className="ri-arrow-right-line text-sm"></i>
          </button>
        </div>
      </div>
    </section>
  );
}